import React, { useState, useEffect } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  Dimensions, Modal, Platform,
} from 'react-native';
import { useSegments, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import AdminGlobalSearch from './AdminGlobalSearch';
import { openExternal } from '../utils/openExternal';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

interface SidebarItem {
  readonly icon: keyof typeof Ionicons.glyphMap;
  readonly label: string;
  readonly route: string;
  readonly external?: string; // external URL — opens in browser instead of navigating
  /** Show only if the admin has at least one of these permission keys. super_admin sees everything. */
  readonly permissions?: readonly string[];
}

interface SidebarSection {
  readonly title: string;
  readonly items: readonly SidebarItem[];
}

const sections: readonly SidebarSection[] = [
  {
    title: 'Platform',
    items: [
      { icon: 'grid', label: 'Dashboard', route: 'dashboard', permissions: ['platform_dashboard'] },
      { icon: 'analytics', label: 'Analytics', route: 'analytics', permissions: ['platform_analytics'] },
      { icon: 'globe', label: 'Web Analytics', route: 'web-analytics', permissions: ['platform_web_analytics'] },
      { icon: 'funnel', label: 'Acquisition', route: 'acquisition', permissions: ['platform_acquisition'] },
      { icon: 'pricetags', label: 'Referrals', route: 'referrals', permissions: ['platform_referrals'] },
      { icon: 'cash', label: 'Commissions', route: 'commissions', permissions: ['platform_referrals'] },
    ],
  },
  {
    title: 'People',
    items: [
      { icon: 'people', label: 'Customers', route: 'users', permissions: ['users'] },
      { icon: 'business', label: 'Owners', route: 'owners', permissions: ['owners'] },
      { icon: 'shield', label: 'Admins', route: 'admins' /* super_admin only */ },
    ],
  },
  {
    title: 'Operations',
    items: [
      { icon: 'location', label: 'Locations', route: 'locations', permissions: ['owner_panel.locations'] },
      { icon: 'card', label: 'Transactions', route: 'transactions', permissions: ['owner_panel.transactions'] },
      { icon: 'return-down-back', label: 'Refunds', route: 'refunds', permissions: ['owner_panel.transactions'] },
      { icon: 'gift', label: 'Vouchers', route: 'vouchers', permissions: ['owner_panel.vouchers'] },
      { icon: 'scan', label: 'Wash Scan', route: 'scan' },
      { icon: 'pricetag', label: 'Products', route: 'products', permissions: ['products'] },
      { icon: 'cube', label: 'Orders', route: 'hardware-orders', permissions: ['hardware_orders'] },
    ],
  },
  {
    title: 'Services',
    items: [
      { icon: 'car', label: 'Fleet Accounts', route: 'fleet-accounts', permissions: ['owner_panel.fleet_accounts'] },
      { icon: 'receipt', label: 'Fleet Invoices', route: 'fleet-invoices', permissions: ['owner_panel.fleet_invoices'] },
    ],
  },
  {
    title: 'Staff',
    items: [
      { icon: 'person', label: 'Attendants', route: 'attendants', permissions: ['owner_panel.attendants'] },
    ],
  },
  {
    title: 'System',
    items: [
      { icon: 'hardware-chip', label: 'Controllers', route: 'controllers', permissions: ['controllers'] },
      { icon: 'terminal', label: 'MQTT Logs', route: 'mqtt-logs', permissions: ['mqtt_logs'] },
      { icon: 'cloud-upload', label: 'Firmware', route: 'firmware', permissions: ['firmware'] },
      { icon: 'people', label: 'Imports', route: 'imports', permissions: ['owner_panel.customers'] }, // 1.4 P27
      { icon: 'chatbox-ellipses', label: 'Feedback', route: 'feedback', permissions: ['feedback'] },
      { icon: 'document-text', label: 'Audit Log', route: 'audit-log', permissions: ['audit_log'] },
      { icon: 'notifications', label: 'Alerts', route: 'alerts', permissions: ['alerts_history'] },
      { icon: 'options', label: 'Alert Preferences', route: 'alert-preferences' },
      { icon: 'create', label: 'Content Pages', route: 'cms', permissions: ['cms'] },
      { icon: 'settings', label: 'Account', route: 'settings' },
    ],
  },
];

/** All admin route segments, derived from sidebar sections. */
export const allAdminRoutes = sections.flatMap(s => s.items.map(i => i.route));

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED = 64;
const BREAKPOINT = 768;

const SUPER_ADMIN_ONLY = ['admins'];

/** AdminSidebar - collapsible sidebar for admin panel; drawer on mobile, fixed on desktop. */
export default function AdminSidebar() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const isSuperAdmin = user?.adminRole === 'super_admin';
  const { hasAny } = useAdminPermissions();
  const styles = useStyles(colors);
  const segments = useSegments();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setWindowWidth(window.width);
      if (window.width >= BREAKPOINT) setDrawerOpen(false);
    });
    return () => sub.remove();
  }, []);

  const isMobile = windowWidth < BREAKPOINT;

  // Segment after 'admin' determines active route
  const roleIndex = segments.indexOf('admin' as never);
  const rawSegment = roleIndex >= 0 ? (segments[roleIndex + 1] as string | undefined) : undefined;
  const currentSegment = rawSegment ?? 'dashboard';

  const navigateTo = (route: string) => {
    router.replace(`/admin/${route}` as never);
    if (isMobile) setDrawerOpen(false);
  };

  const sidebarContent = (
    <ScrollView
      style={[styles.sidebarInner, { paddingTop: insets.top + spacing.sm }]}
      contentContainerStyle={[styles.sidebarContent, { paddingBottom: spacing.xl + Math.max(insets.bottom, Platform.OS === 'android' ? 48 : 0) }]}
      showsVerticalScrollIndicator={false}
    >
      {/* Logo - hardcoded brand hex values */}
      <View style={styles.logoRow}>
        {!collapsed || isMobile ? (
          <Text style={styles.logoText}>
            Rinse<Text style={styles.logoPay}>Pay</Text>
            <Text style={styles.logoAdmin}> Admin</Text>
          </Text>
        ) : (
          <Text style={styles.logoText}>
            R<Text style={styles.logoPay}>P</Text>
          </Text>
        )}
      </View>

      {!isMobile && (
        <TouchableOpacity
          style={styles.collapseBtn}
          onPress={() => setCollapsed(!collapsed)}
          accessibilityLabel={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Ionicons
            name={collapsed ? 'chevron-forward' : 'chevron-back'}
            size={18}
            color={colors.textLight}
          />
        </TouchableOpacity>
      )}

      {(!collapsed || isMobile) && (
        <AdminGlobalSearch onNavigate={() => { if (isMobile) setDrawerOpen(false); }} />
      )}

      {(isSuperAdmin ? sections : sections
        .map(s => ({
          ...s,
          items: s.items.filter(i => {
            if (SUPER_ADMIN_ONLY.includes(i.route)) return false;
            if (!i.permissions || i.permissions.length === 0) return true;
            return hasAny(i.permissions);
          }),
        }))
        .filter(s => s.items.length > 0)
      ).map(section => (
        <View key={section.title} style={styles.section}>
          {(!collapsed || isMobile) && (
            <Text style={styles.sectionTitle}>{section.title}</Text>
          )}
          {section.items.map(item => {
            const isActive = currentSegment === item.route;
            return (
              <TouchableOpacity
                key={item.route}
                style={[styles.navItem, isActive && !item.external && styles.navItemActive, (collapsed && !isMobile) && { justifyContent: 'center', paddingHorizontal: 0 }]}
                onPress={() => {
                  if (item.external) {
                    openExternal(item.external);
                  } else {
                    navigateTo(item.route);
                  }
                }}
                accessibilityRole={item.external ? 'link' : 'tab'}
                accessibilityState={item.external ? undefined : { selected: isActive }}
                accessibilityLabel={item.label}
              >
                <Ionicons
                  name={item.icon}
                  size={20}
                  color={isActive && !item.external ? colors.primary : colors.textLight}
                />
                {(!collapsed || isMobile) && (
                  <Text
                    style={[styles.navLabel, isActive && !item.external && styles.navLabelActive]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                )}
                {item.external && (!collapsed || isMobile) && (
                  <Ionicons name="open-outline" size={14} color={colors.textLight} />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
    </ScrollView>
  );

  if (isMobile) {
    return (
      <>
        <View style={[styles.mobileBar, { paddingTop: insets.top }]}>
          <TouchableOpacity onPress={() => setDrawerOpen(true)} style={styles.hamburger}>
            <Ionicons name="menu" size={26} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.mobileTitle}>
            Rinse<Text style={styles.logoPay}>Pay</Text>
            <Text style={styles.logoAdmin}> Admin</Text>
          </Text>
          <View style={{ width: 40 }} />
        </View>
        <Modal
          visible={drawerOpen}
          transparent
          animationType="none"
          onRequestClose={() => setDrawerOpen(false)}
        >
          <TouchableOpacity
            style={styles.overlay}
            activeOpacity={1}
            onPress={() => setDrawerOpen(false)}
          >
            <TouchableOpacity activeOpacity={1} style={styles.drawerContainer}>
              <View style={[styles.sidebar, { width: SIDEBAR_WIDTH }]}>
                {sidebarContent}
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      </>
    );
  }

  return (
    <View style={[styles.sidebar, { width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH }]}>
      {sidebarContent}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  sidebar: {
    backgroundColor: c.surface,
    borderRightWidth: 1,
    borderRightColor: c.borderLight,
    height: '100%',
  },
  sidebarInner: {
    flex: 1,
  },
  sidebarContent: {
    paddingBottom: spacing.xl,
  },
  logoRow: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  logoText: {
    fontSize: 22,
    fontWeight: '800',
    color: '#245f73',
    letterSpacing: 1,
  },
  logoPay: {
    color: '#733e24',
  },
  logoAdmin: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '400',
  },
  collapseBtn: {
    alignSelf: 'flex-end',
    padding: spacing.xs,
    marginRight: spacing.sm,
    marginBottom: spacing.xs,
  },
  section: {
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    color: c.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    marginTop: spacing.xs,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.xs,
    borderRadius: borderRadius.sm,
    gap: spacing.sm,
    minHeight: 40,
  },
  navItemActive: {
    backgroundColor: c.primaryBg,
  },
  navLabel: {
    fontSize: fontSize.sm,
    color: c.textLight,
    fontWeight: fontWeight.medium,
    flex: 1,
  },
  navLabelActive: {
    color: c.primary,
    fontWeight: fontWeight.semibold,
  },
  mobileBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: c.surface,
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  hamburger: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 20,
    fontWeight: '800',
    color: '#245f73',
    letterSpacing: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    flexDirection: 'row',
  },
  drawerContainer: {
    width: SIDEBAR_WIDTH,
    height: '100%',
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
